import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';

interface MaintenanceRequestCardProps {
  openTicketsCount: number;
  isLoading?: boolean;
  onPress: () => void;
}

export const MaintenanceRequestCard: React.FC<MaintenanceRequestCardProps> = ({
  openTicketsCount,
  isLoading = false,
  onPress,
}) => {
  // Get status text based on open tickets count
  const getStatusText = (): string => {
    if (openTicketsCount === 0) {
      return 'No open requests';
    }
    if (openTicketsCount === 1) {
      return '1 open request';
    }
    return `${openTicketsCount} open requests`;
  };

  // Get icon background color based on state
  const getIconBackground = () => {
    if (openTicketsCount > 0) {
      return { backgroundColor: 'rgba(255, 118, 41, 0.15)' }; // Orange tint for active requests
    }
    return { backgroundColor: MARKETPLACE_COLORS.white10Percent }; // Default
  };

  // Get icon color based on state
  const getIconColor = () => {
    if (openTicketsCount > 0) {
      return MARKETPLACE_COLORS.productivityOrange; // Orange for active requests
    }
    return MARKETPLACE_COLORS.accentBlueSolid; // Blue
  };

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Maintenance: ${getStatusText()}`}
    >
      <View style={styles.content}>
        {/* Icon */}
        <View style={[styles.icon, getIconBackground()]}>
          <Icon
            name="construct-outline"
            size={20}
            color={getIconColor()}
          />
        </View>

        {/* Text Content */}
        <View style={styles.textContent}>
          <Text style={styles.title}>Maintenance</Text>
          
          {isLoading ? (
            <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textMuted} style={styles.loader} />
          ) : (
            <Text style={styles.status}>{getStatusText()}</Text>
          )}
        </View>

        {/* Chevron */}
        <Icon
          name="chevron-forward"
          size={24}
          color={MARKETPLACE_COLORS.inputBorder}
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  textContent: {
    flex: 1,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: 2,
  },
  status: {
    fontSize: 13,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textMuted,
  },
  loader: {
    alignSelf: 'flex-start',
  },
});
